import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { Participant } from '@/lib/crm/components/ParticipantPillList';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { useStoreResolvedParticipants } from '@/lib/crm/hooks/useStoreResolvedParticipants';
import {
  EMAIL_INDEX_UNKNOWN,
  getEmailIndexSnapshot,
  subscribeToEmailIndex,
} from '@/lib/crm/store/crmObjectStore';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { ProposalDetail } from '@/lib/proposals/core/types';
import {
  getTriggerActionIds,
  useTriggerPreview,
} from '@/lib/proposals/hooks/useTriggerPreview';

import {
  deriveActionRecipientEmails,
  deriveFirstEmailThreadTriggerEntityId,
  deriveTriggerButtons,
  deriveTriggerParticipantEmails,
  entityButtonDescriptor,
  isTriggerButtonRecordOpen,
  proposalHasTrigger,
  resolveTriggerUrl,
  sortTriggerButtons,
  type TriggerButtonDescriptor,
  type TriggerButtonTarget,
} from './ActionOverview.helpers';

interface TriggerShortcutButtonsState {
  /** True while the trigger fetch that derives the buttons is in flight. */
  isLoading: boolean;
  /** The "View X" buttons derived from the proposal's standard triggers. */
  buttons: TriggerButtonDescriptor[];
  /** Activate a button: open the entity slideout (or a validated URL). */
  onActivate: (target: TriggerButtonTarget) => void;
  /**
   * True while any email participant's store resolution is still UNKNOWN —
   * a "View {contact}" button may still appear. `buttons` being empty is
   * only final once BOTH this and `isLoading` are false. Consumed by
   * AutoOpenFirstRecord's no-trigger-buttons fallback; the header itself
   * deliberately ignores it (buttons pop in as they resolve).
   */
  isResolvingParticipants: boolean;
  /**
   * Entity id of the first CRM-event trigger whose entity is an email
   * thread, or null. Email-thread triggers render as their own card and get
   * NO header button, so AutoOpenFirstRecord uses this as the fallback
   * context to open when the header has no trigger buttons.
   */
  firstTriggerEmailThreadEntityId: string | null;
}

/**
 * True once every email in `emails` has a settled store resolution — an
 * entity id or a confirmed negative — i.e. no lookup is still UNKNOWN.
 * Subscribes to the store's per-email index so the flag flips as results
 * (including negatives, which change no participant object) land.
 */
function useParticipantsResolutionSettled(emails: readonly string[]): boolean {
  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribes = emails.map(email =>
        subscribeToEmailIndex(email, listener)
      );
      return () => {
        for (const unsubscribe of unsubscribes) {
          unsubscribe();
        }
      };
    },
    [emails]
  );
  const getSnapshot = useCallback(
    () =>
      emails.every(
        email => getEmailIndexSnapshot(email) !== EMAIL_INDEX_UNKNOWN
      ),
    [emails]
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Derives the "View X" trigger shortcut buttons for a proposal — rendered in
 * the detail header beside the title. Fetches the same trigger query the
 * high-priority TriggerPreview card uses (React Query dedupes, so there's no
 * double request).
 */
export function useTriggerShortcutButtons(
  proposal: ProposalDetail
): TriggerShortcutButtonsState {
  const slideoutStackContext = useSlideoutStackContextOptional();
  const openSlideout = slideoutStackContext?.openSlideout;
  const closeFromEntry = slideoutStackContext?.closeFromEntry;
  const stack = slideoutStackContext?.stack;

  // The trigger is a property of the ORIGINAL proposal, keyed off its primary
  // action id — see getTriggerActionIds. Callers pass the unfiltered proposal.
  const { fetchActionId, cacheActionId } = useMemo(
    () => getTriggerActionIds(proposal),
    [proposal]
  );

  const {
    data: triggers,
    isLoading,
    isPlaceholderData,
  } = useTriggerPreview(proposal.org, proposal.id, fetchActionId, {
    enabled: fetchActionId > 0,
    cacheActionId,
  });

  // keepPreviousData holds the previously-viewed proposal's triggers while a
  // switch fetches. That stale data belongs to a different proposal, so
  // ignore it — otherwise the header would briefly render (clickable) buttons
  // that open the prior proposal's entities.
  const currentTriggers = isPlaceholderData ? undefined : triggers;

  const crmButtons = useMemo(
    () =>
      Array.isArray(currentTriggers)
        ? deriveTriggerButtons(currentTriggers)
        : [],
    [currentTriggers]
  );

  // Email participants come from two sources: plain email triggers (from/to/
  // cc — an inbound email being acted on) and email-send action payloads
  // (to/cc — a new outbound email has no email trigger at all). Both carry
  // raw addresses only; resolve them against the CRM store's email index so
  // the participants that ARE contacts surface as "View {name}" buttons
  // alongside the crm-event-derived ones.
  const participantEmails = useMemo(() => {
    const emails = new Set(deriveActionRecipientEmails(proposal));
    if (Array.isArray(currentTriggers)) {
      for (const email of deriveTriggerParticipantEmails(currentTriggers)) {
        emails.add(email);
      }
    }
    return [...emails];
  }, [proposal, currentTriggers]);
  const emailParticipants = useMemo<Participant[]>(
    () => participantEmails.map(email => ({ key: email, email })),
    [participantEmails]
  );
  const resolvedParticipants = useStoreResolvedParticipants({
    participants: emailParticipants,
    orgId: proposal.org,
  });
  const participantsSettled =
    useParticipantsResolutionSettled(participantEmails);

  const buttons = useMemo(() => {
    const merged = [...crmButtons];
    const seen = new Set(merged.map(b => b.key));
    for (const participant of resolvedParticipants) {
      const { object } = participant;
      // Contacts only: the org's own users also resolve through the email
      // index, but "View {yourself}" is noise in the header.
      if (
        !object ||
        object.entity_type_name !== 'ahoy_contact' ||
        seen.has(object.entity_id)
      ) {
        continue;
      }
      seen.add(object.entity_id);
      merged.push(
        entityButtonDescriptor(
          object.entity_type_name,
          object.entity_id,
          getDisplayName(object)
        )
      );
    }
    return sortTriggerButtons(merged);
  }, [crmButtons, resolvedParticipants]);

  // Gate `isPlaceholderData` on `fetchActionId > 0`: a trigger-marked
  // proposal with no action leaves the query disabled (never fetches), yet
  // keepPreviousData holds prior data with isPlaceholderData stuck true — so
  // without the gate the loading state would never settle.
  const hasTrigger = proposalHasTrigger(proposal);
  const isLoadingButtons =
    (isLoading || (isPlaceholderData && fetchActionId > 0)) && hasTrigger;

  const onActivate = useCallback(
    (target: TriggerButtonTarget) => {
      if (target.kind === 'entity') {
        // No-op outside a SlideoutStackProvider (e.g. Storybook); wired live
        // once mounted in the proposal panel.
        //
        // Toggle: a highlighted button's record is already open on the
        // stack, so a second click closes it (and anything layered above
        // it) rather than re-opening it. Uses the exact predicate that
        // drives the button's highlighted state, so highlight and toggle
        // can never disagree.
        if (isTriggerButtonRecordOpen(target, stack)) {
          closeFromEntry?.(target.entityId);
          return;
        }
        // App-level open, NOT a push: clicking a header button while another
        // record is already layered REPLACES it instead of stacking. The
        // hosting proposal slideout (when the proposal is one) stays put as
        // the `base` — the record opens directly on top of it. Only clicks
        // from INSIDE a slideout (e.g. entity badges) build the stack.
        openSlideout?.(target.entityId, {
          base: { kind: 'proposal', id: String(proposal.id) },
        });
      } else {
        // No current trigger-derivation path produces a `kind: 'url'` target
        // (see ActionOverview.helpers.ts), but this is the enforcement point
        // for the discriminated union's `url` variant — validate before ever
        // handing a url to `window.open`.
        const resolvedUrl = resolveTriggerUrl(target.url);
        if (resolvedUrl) {
          window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
        }
      }
    },
    [openSlideout, closeFromEntry, stack, proposal.id]
  );

  const firstTriggerEmailThreadEntityId = useMemo(
    () =>
      Array.isArray(currentTriggers)
        ? deriveFirstEmailThreadTriggerEntityId(currentTriggers)
        : null,
    [currentTriggers]
  );

  return {
    isLoading: isLoadingButtons,
    buttons,
    onActivate,
    isResolvingParticipants: !participantsSettled,
    firstTriggerEmailThreadEntityId,
  };
}
